import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Spinner from '@/components/ui/Spinner';
import UploadableDocCard from '@/components/project/UploadableDocCard';
import { useBibleGeneration } from '@/hooks/useBibleGeneration';
import { useScriptUpload } from '@/hooks/useScriptUpload';
import type { Project } from '@/hooks/useProject';

/**
 * What the crew is given to work from: the screenplay and the creative brief.
 *
 * Laid out side by side rather than stacked — they are read together, and stacking
 * two mostly-empty cards was what left the old layout with a column of dead space.
 */
export default function SourceMaterial({
  project,
  onUploaded,
  onUploadBrief,
}: {
  project: Project;
  onUploaded: () => Promise<void>;
  onUploadBrief: (file: File) => Promise<void>;
}) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { generating, generate } = useBibleGeneration(project.id);
  const { upload: uploadScript } = useScriptUpload(project.id, onUploaded);

  const isFootage = (project.production_type || 'generative') === 'footage';
  const script = project.source_script;
  const hasScript = !!(project.source_script || project.script);
  const hasBible = !!project.script?.trim();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {isFootage ? (
          <Card className="lg:col-span-2">
            <CardContent className="pt-4 text-sm leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">{t('project.footageNoticeTitle')}</span> —{' '}
              {t('project.footageNotice')}
            </CardContent>
          </Card>
        ) : (
          <UploadableDocCard
            title={t('script.title')}
            help={t('script.help')}
            defaultOpen
            meta={
              script
                ? t('script.meta', { lines: script.split('\n').length, kb: (script.length / 1024).toFixed(1) })
                : t('script.authoredIn')
            }
            accept=".txt,text/plain"
            uploadLabel={t('script.upload')}
            emptyText={t('script.empty')}
            onUpload={uploadScript}
          >
            {script ? (
              <pre className="max-h-[420px] overflow-y-auto whitespace-pre-wrap font-mono text-xs text-muted-foreground">
                {script.slice(0, 20000)}
                {script.length > 20000 ? `\n${t('script.truncated')}` : ''}
              </pre>
            ) : undefined}
          </UploadableDocCard>
        )}

        <UploadableDocCard
          title={t('brief.title')}
          help={t('brief.help')}
          defaultOpen
          meta={t('script.authoredIn')}
          accept=".txt,.md,text/plain,text/markdown"
          uploadLabel={t('brief.upload')}
          emptyText={t('brief.empty')}
          onUpload={onUploadBrief}
        >
          {project.creative_brief || project.creative_brief_preview ? (
            <p className="max-h-[420px] overflow-y-auto whitespace-pre-wrap text-sm text-muted-foreground">
              {project.creative_brief || project.creative_brief_preview}
            </p>
          ) : undefined}
        </UploadableDocCard>
      </div>

      {!isFootage && (
        <Button variant="outline" size="sm" onClick={generate} disabled={generating || !hasScript}>
          {generating ? <Spinner className="mr-2" /> : null}
          {/* "Regenerate" only once one exists — offering to RE-do something you have
              never done is the kind of wording that makes a required step look
              optional. */}
          {generating ? t('launch.generatingBible') : hasBible ? t('launch.regenerateBible') : t('launch.generateBible')}
        </Button>
      )}
    </div>
  );
}
